// Modules
import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { orderBy } from "lodash";

// Redux
import { selectedFilterData } from "redux/userList/userSlice";
import { getReimbDataForAdmin, reimbSummaryListForAdminSelector, getLoadingStateForReimbSummaryList } from "redux/reimbursement/reimbursementSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Constant
import { Filters as FilterConstant } from "constants/filters";
import { REIMBURSMENT_DECL_VERIFICATION as Constants, Routes, DateTimeFormats } from "constants/global";
import { statusMap } from "containers/ReimbursementVerification/useReimbursementVerification";

// Component
import ViewEdit from "components/atoms/ViewEdit/ViewEdit";
import Status from "components/atoms/Status/Status";

// Type
import { ReimbSummaryDataType } from "redux/types";
import { GroupDetailsType } from "components/atoms/ExpandedRowTable/ExpandedRowTable";

// styles
import styles from "containers/ReimbursementVerification/reimbursementVerification.module.css";

export type ColumnHeaderRowType = {
	title: string;
	dataIndex: string;
	align?: "left" | "right" | "center";
};

export type TableDataType = {
	[key: string]: string;
};

export type FilterSortType = {
	[key: string]: string;
};

export type ClaimListPayloadType = {
	from_receipt_date: string;
	to_receipt_date: string;
	page_number: number;
	page_records: number;
	from_net_amount: number;
	to_net_amount: number;
};
export type ExpandedRowTableProps = {
	handlePagination?: (pageNumber: number, groupCode?: string) => void;
	columnHeaderData?: Array<ColumnHeaderRowType>; // Type for column headers
	tableData?: Array<TableDataType>; // Use || to add additional Table Data types
	rowsPerPage?: number;
	groupDetails?: GroupDetailsType;
	searchUser?: string;
};

type ReimGroupType = {
	[key: string]: Array<ReimbSummaryDataType>;
};

type ReimbDataType = {
	key: number;
	declared_on: string;
	tenant_name: string;
	outlet_name: string;
	net_amount: number;
	tenant_amount: number;
	reimb_amount: number;
	reimb_status: JSX.Element;
	review: JSX.Element;
	status: string;
};
export type UserListPayloadType = { page_number: number; page_records: number; user_search_text?: string; group_code?: string };

export const getVerifierStatus = (status: string) => {
	switch (status) {
		case "X":
			return <span className={styles?.rejectedStatus}>{Constants.VERIFIER_REJECTED_STATUS}</span>;
		case "":
			return <span className={styles?.activeStatus}>{Constants.VERIFIER_APPROVED_STATUS}</span>;
	}
};

export const useExpandedRowTableForReimVerification = ({ groupDetails }: ExpandedRowTableProps) => {
	const reimGroup: ReimGroupType = {};
	const pageSize = Constants.DEFAULT_ROW_PER_PAGE;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const reimbSummaryList = useAppSelector(reimbSummaryListForAdminSelector);
	const selectedFilter = useAppSelector(selectedFilterData);
	const loading = useAppSelector(getLoadingStateForReimbSummaryList) as unknown as number;

	const totalRecordCount = useMemo(() => {
		if (reimbSummaryList?.summaryList && groupDetails && reimbSummaryList.summaryList[pathname + groupDetails?.voucher_group_code]) {
			return reimbSummaryList.summaryList[pathname + groupDetails?.voucher_group_code]?.record_count;
		}
	}, [reimbSummaryList]);

	// On pagination getRetroClaimList will be called
	const handlePagination = (pageNumber: number, group?: GroupDetailsType) => {
		const payload = {
			reqPayload: {
				page_number: pageNumber,
				page_records: Constants.DEFAULT_ROW_PER_PAGE,
				from_year_month: moment(moment().subtract(6, "months").calendar()).format("YYYYMM"),
				to_year_month: moment(moment().subtract(1, "months").calendar()).format("YYYYMM"),
				reimb_status: statusMap[pathname],
				voucher_group_code: group?.voucher_group_code
			},
			pathName: pathname
		};
		dispatch(getReimbDataForAdmin(payload));
	};

	useEffect(() => {
		const payload = {
			reqPayload: {
				page_number: 1,
				page_records: Constants.DEFAULT_ROW_PER_PAGE,
				from_year_month: moment(moment().subtract(6, "months").calendar()).format("YYYYMM"),
				to_year_month: moment(moment().subtract(1, "months").calendar()).format("YYYYMM"),
				reimb_status: statusMap[pathname],
				voucher_group_code: groupDetails?.voucher_group_code
			},
			pathName: pathname
		};
		dispatch(getReimbDataForAdmin(payload));
	}, [pathname]);

	const getSortedList = (reimbData: Array<ReimbDataType>, selectedFilterVal: string) => {
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[0].value || selectedFilterVal === "") {
			return reimbData.sort((obj1, obj2) => new Date(obj2.declared_on).setHours(0, 0, 0, 0) - new Date(obj1.declared_on).setHours(0, 0, 0, 0));
		}
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[1].value) {
			return reimbData.sort((obj1, obj2) => new Date(obj1.declared_on).setHours(0, 0, 0, 0) - new Date(obj2.declared_on).setHours(0, 0, 0, 0));
		}
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[4].value) {
			return orderBy(reimbData, ["outlet_name"], ["asc"]);
		}
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[5].value) {
			return orderBy(reimbData, ["outlet_name"], ["desc"]);
		}
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[2].value) {
			return orderBy(reimbData, ["tenant_name"], ["asc"]);
		}
		if (selectedFilterVal === FilterConstant.REIMB_CLAIM[0].values[3].value) {
			return orderBy(reimbData, ["tenant_name"], ["desc"]);
		}
	};

	// handle review page

	const handleReviewNavigation = (item: ReimbSummaryDataType) => {
		navigate(Routes.REIMBURSEMENT_CLAIM_REVIEW, { state: { reimb_summ_id: item?.reimb_summ_id } });
	};
	// Updating the userlist based on the group
	const updatedList = useMemo(() => {
		if (groupDetails && reimbSummaryList?.summaryList[pathname + groupDetails?.voucher_group_code]) {
			const reimbData: Array<ReimbDataType> = [];

			reimGroup[groupDetails?.voucher_group_code] =
				reimbSummaryList.summaryList[pathname + groupDetails?.voucher_group_code] && reimbSummaryList.summaryList[pathname + groupDetails?.voucher_group_code].reimb_summ;

			reimGroup[groupDetails?.voucher_group_code]?.map((item: ReimbSummaryDataType) => {
				const reimbDetails = {
					key: item.reimb_summ_id,
					declared_on: moment(item?.declared_on)?.format(DateTimeFormats.USE_DD_MMM),
					tenant_name: item.tenant_name.trim(),
					outlet_name: item.outlet_name.trim(),
					net_amount: item.net_amount,
					tenant_amount: item.tenant_amount,
					reimb_amount: item.reimb_amount,
					verifier_status: getVerifierStatus(item.verifier_rejected),
					reimb_status: <Status status={item.reimb_status.trim().toLowerCase()} />,
					review: <ViewEdit onClick={() => handleReviewNavigation(item)} />,
					status: item.reimb_status.trim().toUpperCase()?.replace("_", " "),
					yearmonth: item?.yearmonth,
					outlet_code: item?.outlet_code
				};
				reimbData.push(reimbDetails);
			});
			const selectedFilterVal: string = selectedFilter[groupDetails.voucher_group_desc] ?? "";
			return getSortedList(reimbData, selectedFilterVal);
		}
	}, [reimbSummaryList, selectedFilter, pathname]);

	return {
		handlePagination,
		updatedList,
		loading,
		pageSize,
		totalRecordCount
	};
};
